import React, { useContext, useEffect, useState, } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { MainNavigationContext } from '../MainNavigationContext';
import { MIN_WIDTH_MAIN_NAVIGATION_IN_PX } from '../constants';
import { useIsMobileNavigation } from '../hooks/useIsMobileNavigation';
import { isClipPathForSvgsSupported } from '../helpers/isClipPathForSvgsSupported';
export const HEIGHT_VIEWBOX_LARGE_SWUNG_IN_PX = 91;
export const HEIGHT_VIEWBOX_SMALL_SWUNG_IN_PX = 53;
const SvgWrapper = styled.svg `
  position: absolute;
  left: 0;
  bottom: 0;
  top: 0;
  right: 0;
  pointer-events: none;
  z-index: -1;
  width: 100%;
  height: ${props => props.isLargeSwung
    ? HEIGHT_VIEWBOX_LARGE_SWUNG_IN_PX
    : HEIGHT_VIEWBOX_SMALL_SWUNG_IN_PX}px;
`;
const AnimatedRect = styled.rect `
  transition-property: x, y, width;
  transition-duration: 0.3s;
`;
export const SwungBorder = ({ highlightPosition, navigationWidth = 0, }) => {
    const theme = useContext(ThemeContext);
    const { distanceBetweenPrimaryNavigationItemsInRem } = useContext(MainNavigationContext);
    const isLargeSwung = !useIsMobileNavigation();
    const [isSwungShown, setIsSwungShown] = useState(false);
    /**
     * Safari 13.0 (and probably lower) doesn't support clipPath for SVG elements.
     * Therefore, we use a different indicator based on a simple <rect> element.
     * See https://boxine.atlassian.net/browse/TOC-3069 for details.
     */
    const isClipPathSupported = isClipPathForSvgsSupported();
    /**
     * Make this component hydrateable to allow prerendering on the server (SSR).
     * As we rewrite the path definition of the swung depending on the viewport
     * size, the server would need to have this exact viewport size so that the
     * markup matches 100% and the hydration process works without issues. By
     * using an effect (which isn't executed on the server) we remove the swung
     * path from the markup to be hydrated. On the client, the swung will be
     * inserted during the first rendering.
     */
    useEffect(() => {
        setIsSwungShown(true);
    }, []);
    // Avoid strange gradients on very small screens
    const minViewportWidthInPixel = navigationWidth < MIN_WIDTH_MAIN_NAVIGATION_IN_PX
        ? MIN_WIDTH_MAIN_NAVIGATION_IN_PX
        : navigationWidth;
    const largeSwungPath = `M${minViewportWidthInPixel},65 L91.9838324,90.2753031 C42.1261592,91.2101345 0.950698684,51.5503351 0.0158673112,1.69266193 L0.00396691493,0.846368152 L0.00396691493,0.846368152 L0.00366508445,0.781970575 L0.00366508445,0.781970575 L0.0146600158,1.56387244 C0.878362914,47.628027 38.9209079,84.270233 84.9850625,83.4065301 L84.9850625,83.4065301 L${minViewportWidthInPixel},58 L${minViewportWidthInPixel},65 Z`;
    const largeSwungPathWhite = `M0,0 L107.515625,0 L${minViewportWidthInPixel},0 L${minViewportWidthInPixel},58 L84.9850625,83.4065301 C38.9209079,84.270233 0.878362914,47.628027 0.0146600158,1.56387244 C0.00488688662,1.04263889 6.38439199e-17,0.521325169 0,0 L0,0 L0,0 Z`;
    const smallSwungPath = `M${minViewportWidthInPixel},39.8769231 L50.9638694,52.0806223 C23.8573903,53.101494 1.05566714,31.9549309 0.0347954342,4.84845174 C0.0116005326,4.23257405 7.54767359e-17,3.61631432 0,3 L0.0914746776,3.00059741 C1.7047047,29.5515707 24.2426583,50.0869842 50.9638694,49.0806223 L50.9638694,49.0806223 L${minViewportWidthInPixel},36.8769231 L${minViewportWidthInPixel},39.8769231 Z`;
    const smallSwungPathWhite = `M0,0 L${minViewportWidthInPixel},0 L${minViewportWidthInPixel},36.8769231 L50.9638694,49.0806223 C23.8573903,50.101494 1.05566714,28.9549309 0.0347954342,1.84845174 C0.0116005326,1.23257405 7.54767359e-17,0.61631432 0,0 L0,0 L0,0 Z`;
    const distanceOutsideActiveItemBoxInPixel = distanceBetweenPrimaryNavigationItemsInRem > 0
        ? (distanceBetweenPrimaryNavigationItemsInRem * 16) / 2
        : 0;
    if (!isSwungShown) {
        return null;
    }
    const { left, width } = highlightPosition;
    return (React.createElement(SvgWrapper, { viewBox: `0 0 ${minViewportWidthInPixel} ${isLargeSwung
            ? HEIGHT_VIEWBOX_LARGE_SWUNG_IN_PX
            : HEIGHT_VIEWBOX_SMALL_SWUNG_IN_PX}`, isLargeSwung: isLargeSwung, xmlns: "http://www.w3.org/2000/svg", preserveAspectRatio: "none" }, isLargeSwung ? (React.createElement(React.Fragment, null,
        isClipPathSupported && (React.createElement("defs", null,
            React.createElement("clipPath", { id: "cutoff-border" },
                React.createElement("path", { d: largeSwungPath })))),
        React.createElement("path", { d: largeSwungPath, fill: "#D1D1D4", fillRule: "nonzero" }),
        React.createElement("path", { d: largeSwungPathWhite, fill: "#FFF" }),
        isClipPathSupported ? (React.createElement(AnimatedRect, { x: left - distanceOutsideActiveItemBoxInPixel, y: "0", width: left > 0 ? width + distanceOutsideActiveItemBoxInPixel * 2 : 0, height: "100", fill: theme.BrandPrimary, clipPath: "url(#cutoff-border)" })) : (React.createElement("rect", { x: left - distanceOutsideActiveItemBoxInPixel, y: 0, width: left > 0 ? width + distanceOutsideActiveItemBoxInPixel * 2 : 0, height: "4", fill: theme.BrandPrimary })))) : (React.createElement(React.Fragment, null,
        React.createElement("path", { d: smallSwungPath, fill: "#D1D1D4" }),
        React.createElement("path", { d: smallSwungPathWhite, fill: "#FFF" })))));
};
